import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    serviceType: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // For now, just show success message (mock submission)
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        organization: '',
        serviceType: '',
        message: ''
      });
    }, 3000);
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="container">
            <h1 className="display-large">Contact Us</h1>
            <p className="body-large">
              Get in touch for a free site assessment and custom quote
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content-section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-container">
              <h2 className="heading-2">Request Free Site Assessment</h2>
              <p className="body-medium form-description">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>

              {isSubmitted ? (
                <div className="success-message">
                  <CheckCircle size={48} />
                  <h3 className="heading-3">Thank You!</h3>
                  <p className="body-medium">
                    Your request has been received. We'll contact you shortly.
                  </p>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="organization" className="form-label">Organization / Farm / School</label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="serviceType" className="form-label">Service Interested In *</label>
                    <select
                      id="serviceType"
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleChange}
                      className="form-select"
                      required
                    >
                      <option value="">Select a service</option>
                      <option value="power">Power-Independent IT Services</option>
                      <option value="internet">Off-Grid Internet & Connectivity</option>
                      <option value="security">Security & Surveillance</option>
                      <option value="it-support">IT Support</option>
                      <option value="smart-solutions">Smart Off-Grid Solutions</option>
                      <option value="training">Training & Capacity Building</option>
                      <option value="maintenance">Maintenance Contracts</option>
                      <option value="hardware">Hardware Sales & Rentals</option>
                      <option value="other">Other / Multiple Services</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message" className="form-label">Message / Requirements *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className="form-textarea"
                      rows="5"
                      required
                    ></textarea>
                  </div>

                  <button type="submit" className="btn-primary btn-submit">
                    <Send size={20} />
                    Submit Request
                  </button>
                </form>
              )}
            </div>

            {/* Contact Information */}
            <div className="contact-info-container">
              <h2 className="heading-2">Get In Touch</h2>
              
              <div className="contact-info-cards">
                <div className="info-card">
                  <div className="info-icon">
                    <Phone size={28} />
                  </div>
                  <h3 className="heading-3">Phone & WhatsApp</h3>
                  <a href="tel:+27724603624" className="info-link">
                    +27 72 460 3624
                  </a>
                  <a
                    href="https://wa.me/27724603624"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary btn-small"
                  >
                    WhatsApp Us
                  </a>
                </div>

                <div className="info-card">
                  <div className="info-icon">
                    <Mail size={28} />
                  </div>
                  <h3 className="heading-3">Email</h3>
                  <a href="mailto:offgridsolution@gmail.com" className="info-link">
                    offgridsolution@gmail.com
                  </a>
                  <p className="body-small info-note">
                    We respond within 24 hours
                  </p>
                </div>

                <div className="info-card">
                  <div className="info-icon">
                    <MapPin size={28} />
                  </div>
                  <h3 className="heading-3">Service Areas</h3>
                  <p className="body-medium">
                    South Africa<br />
                    Botswana<br />
                    Namibia
                  </p>
                </div>
              </div>

              <div className="why-contact-card">
                <h3 className="heading-3">Why Contact Us?</h3>
                <ul className="why-contact-list">
                  <li>Free site assessment & consultation</li>
                  <li>Custom solutions for your needs</li>
                  <li>Transparent pricing & quotes</li>
                  <li>Serving rural & remote areas</li>
                  <li>Experienced local team</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;